import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef } from 'react'
import { useReducedMotion } from 'framer-motion'
import { cn } from '@/lib/utils'
import { CENTER, CUTS, ringPath, ryFor, surfaceCy, WAVE_W, type Cut } from './ripple-geometry'
import { DURATION, frameAt } from './ripple-animation'

/**
 * EventRipple-merket, retning E. Se `design/brand/DIRECTION-E.md`.
 *
 * Dråpen faller på skrå, ringvirkningen går ut i perspektiv, kameraet retter
 * seg opp, og det som blir stående er logoen sett rett ovenfra.
 *
 * Formen kommer fra `ripple-geometry.ts` og bevegelsen fra
 * `ripple-animation.ts`. Begge deles med `scripts/build-brand-assets.mjs`,
 * som skriver SVG-filene, så det som animeres og det som ligger på disk kan
 * ikke bli to forskjellige ting.
 *
 * Hvorfor requestAnimationFrame og ikke CSS: kamerabevegelsen endrer
 * ringenes FORM, ikke bare størrelsen deres. En ellipse som retter seg ut til
 * en sirkel samtidig som streken tynnes ut mot halen, er en ny bane for hvert
 * bilde. Det kan ikke uttrykkes som en transform, og en transform ville
 * dessuten gjort streken tykkere etter hvert som bølgen vokser, altså motsatt
 * av hva en ringvirkning gjør.
 *
 * Hvilebildet er `frameAt(DURATION)`. Når animasjonen er ferdig, står
 * elementene nøyaktig der de sto før den startet, så den kan spilles om igjen
 * når som helst uten at noe hopper.
 */

export interface RippleMarkHandle {
  /** Spill ringvirkningen på nytt. Nullhandling ved redusert bevegelse. */
  replay: () => void
}

interface RippleMarkProps {
  /** Kantlengde i piksler. Merket er kvadratisk. */
  size?: number
  /**
   * Hvilket snitt. Utelates det, velges `small` under 28 piksler, som er der
   * den tredje ringen slutter å være en ring og blir en grå kant.
   */
  cut?: Cut
  /** Spill sekvensen når komponenten monteres. */
  playOnMount?: boolean
  /** Tilgjengelig navn. Uten det er merket dekorativt og skjules for skjermlesere. */
  title?: string
  className?: string
}

export const RippleMark = forwardRef<RippleMarkHandle, RippleMarkProps>(function RippleMark(
  { size = 32, cut, playOnMount = false, title, className },
  ref
) {
  const resolved: Cut = cut ?? (size < 28 ? 'small' : 'full')
  const geometry = CUTS[resolved]

  const ringRefs = useRef<Array<SVGPathElement | null>>([])
  const waveRefs = useRef<Array<SVGEllipseElement | null>>([])
  const dropRef = useRef<SVGGElement | null>(null)
  const rafRef = useRef<number | null>(null)
  const safetyRef = useRef<number | null>(null)
  const prefersReducedMotion = useReducedMotion()

  /** Skriv ett bilde. `ms >= DURATION` gir hvilebildet. */
  const paint = useCallback(
    (ms: number) => {
      const frame = frameAt(ms, geometry)
      const cy = surfaceCy(frame.tilt)

      geometry.rings.forEach((ring, i) => {
        const el = ringRefs.current[i]
        if (!el) return
        const state = frame.rings[i]
        el.setAttribute('d', ringPath(ring, frame.tilt, state.grow))
        el.setAttribute('opacity', String(state.o))
      })

      frame.waves.forEach((wave, i) => {
        const el = waveRefs.current[i]
        if (!el) return
        el.setAttribute('cy', cy.toFixed(2))
        el.setAttribute('rx', wave.rx.toFixed(2))
        el.setAttribute('ry', ryFor(wave.rx, frame.tilt).toFixed(2))
        el.setAttribute('opacity', String(wave.o))
      })

      const drop = dropRef.current
      if (drop) {
        const d = frame.drop
        drop.setAttribute(
          'transform',
          `translate(${CENTER} ${d.cy.toFixed(2)}) scale(${d.sx.toFixed(3)} ${d.sy.toFixed(3)})`
        )
        drop.setAttribute('opacity', String(d.o))
      }
    },
    [geometry]
  )

  const replay = useCallback(() => {
    if (prefersReducedMotion) return

    // En skjult fane kjører ikke requestAnimationFrame i det hele tatt. Uten
    // denne sperren male vi bilde 0, der ringene har dekkevne 0, og så kom
    // aldri bilde 1: merket ble stående usynlig til noe annet trigget en ny
    // avspilling. Åpner man appen i en bakgrunnsfane, var logoen rett og
    // slett borte. Da er det riktige å la hvilebildet stå; «av og til»-
    // regelen spiller den uansett når fanen kommer tilbake.
    if (typeof document !== 'undefined' && document.hidden) return

    if (rafRef.current !== null) cancelAnimationFrame(rafRef.current)
    if (safetyRef.current !== null) window.clearTimeout(safetyRef.current)

    let started: number | null = null
    const finish = () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current)
      rafRef.current = null
      safetyRef.current = null
      paint(DURATION) // eksakt hvilebilde, så neste avspilling starter kjent
    }

    const step = (now: number) => {
      if (started === null) started = now
      const elapsed = now - started
      if (elapsed >= DURATION) return finish()
      paint(elapsed)
      rafRef.current = requestAnimationFrame(step)
    }
    rafRef.current = requestAnimationFrame(step)

    // Belte til seletøyet: blir fanen skjult MENS den spiller, stopper rAF
    // midt i. Tidsavbrudd kjører også i bakgrunnsfaner, om enn strupet, så
    // dette garanterer at merket alltid ender i hvile og aldri fryser halvt.
    safetyRef.current = window.setTimeout(finish, DURATION + 400)
  }, [paint, prefersReducedMotion])

  useImperativeHandle(ref, () => ({ replay }), [replay])

  useEffect(() => {
    if (playOnMount) replay()
    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current)
      if (safetyRef.current !== null) window.clearTimeout(safetyRef.current)
    }
  }, [playOnMount, replay])

  return (
    <svg
      className={cn('shrink-0', className)}
      width={size}
      height={size}
      viewBox="0 0 48 48"
      fill="none"
      role={title ? 'img' : undefined}
      aria-label={title}
      aria-hidden={title ? undefined : true}
    >
      {/* Bølgene. Fødes i sentrum, borte lenge før kanten. */}
      {Array.from({ length: geometry.waves }, (_, i) => (
        <ellipse
          key={`wave-${i}`}
          ref={(el) => {
            waveRefs.current[i] = el
          }}
          cx={CENTER}
          cy={CENTER}
          rx={2}
          ry={2}
          fill="none"
          stroke="currentColor"
          strokeWidth={WAVE_W}
          opacity={0}
        />
      ))}

      {/* Ringene i hvile. Dette er logoen. */}
      {geometry.rings.map((ring, i) => (
        <path
          key={`ring-${i}`}
          ref={(el) => {
            ringRefs.current[i] = el
          }}
          d={ringPath(ring, 1, 1)}
          fill="currentColor"
          opacity={ring.o}
        />
      ))}

      {/* Dråpen, som ender som prikken i midten. */}
      <g ref={dropRef} transform={`translate(${CENTER} ${CENTER}) scale(1 1)`}>
        <circle cx={0} cy={0} r={geometry.drop.r} fill="currentColor" />
      </g>
    </svg>
  )
})
